import { useState } from 'react'
import api from '../services/api'

interface Props {
  isOpen: boolean
  onClose: () => void
  onJoined: () => void
}

const ACKNOWLEDGMENT_TEXT = `By joining the Pet Detection Network, I understand and agree that:
• My location will be stored to identify lost pets near me
• I will receive push notifications when a pet is reported lost within 10 miles
• My approximate location may be shared with pet owners in the area
• I can leave the network and stop notifications at any time`

export default function DetectionNetworkSignup({ isOpen, onClose, onJoined }: Props) {
  const [acknowledged, setAcknowledged] = useState(false)
  const [address, setAddress] = useState('')
  const [latitude, setLatitude] = useState<number | null>(null)
  const [longitude, setLongitude] = useState<number | null>(null)
  const [locationStatus, setLocationStatus] = useState<'idle' | 'loading' | 'done' | 'manual'>('idle')
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  const shareLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('manual')
      return
    }
    setLocationStatus('loading')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude)
        setLongitude(pos.coords.longitude)
        setLocationStatus('done')
      },
      () => setLocationStatus('manual'),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const canJoin = acknowledged && (locationStatus === 'done' || (locationStatus === 'manual' && address))

  const join = async () => {
    setJoining(true)
    setError(null)
    try {
      await api.post('/api/detection-network/join', {
        latitude: latitude ?? 0,
        longitude: longitude ?? 0,
        address: address || null,
        acknowledgment: ACKNOWLEDGMENT_TEXT,
      })
      onJoined()
      onClose()
    } catch {
      setError('Failed to join. Please try again.')
    }
    setJoining(false)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Pet Detection Network</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Help reunite lost pets with their families by joining a community of pet lovers in your area.
          </p>

          {/* How it works */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
            <h3 className="font-semibold text-gray-800 text-sm">How it works</h3>
            {[
              ['We store your location to know which pets are near you', '&#128205;'],
              ['When a pet is lost within 10 miles, you get a push notification', '&#128276;'],
              ['Pet owners may see that network members are nearby', '&#128065;'],
              ['You can leave anytime and notifications stop immediately', '&#10060;'],
            ].map(([text, icon]) => (
              <p key={text} className="text-sm text-gray-600 flex items-start gap-2">
                <span dangerouslySetInnerHTML={{ __html: icon }} />
                {text}
              </p>
            ))}
          </div>

          {/* Location */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 text-sm mb-3">Your Location</h3>
            {locationStatus === 'done' ? (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Location captured
              </div>
            ) : locationStatus === 'manual' ? (
              <input
                type="text"
                placeholder="Enter your city or zip code"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="input-field"
              />
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={shareLocation}
                  disabled={locationStatus === 'loading'}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {locationStatus === 'loading' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <span>&#128205;</span>
                  )}
                  Share Location
                </button>
                <button onClick={() => setLocationStatus('manual')} className="btn-secondary text-sm">
                  Enter Manually
                </button>
              </div>
            )}
          </div>

          {/* Acknowledgment */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-500 whitespace-pre-line mb-4">{ACKNOWLEDGMENT_TEXT}</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="rounded text-primary-500 focus:ring-primary-400 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700">I understand and agree</span>
            </label>
          </div>

          {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

          <button
            onClick={join}
            disabled={!canJoin || joining}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {joining && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            {joining ? 'Joining...' : 'Join the Network'}
          </button>
        </div>
      </div>
    </div>
  )
}
